import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { AuthService } from 'src/app/shared/auth/services/auth.service';
import { PlanoMelhoriaDivergencia } from 'src/app/shared/models/plano-melhoria/planoMelhoriaDivergencia';
import { UserLoginSed } from 'src/app/shared/models/sed/UserLogin';
import { ConvivaErrorHandlingService } from 'src/app/shared/services/convivaErrorHandlingService';
import { PlanoMelhoriaService } from 'src/app/shared/services/plano-melhoria.service';
import { angularCommonImports } from 'src/app/shared/ui/angular-common';
import { convivaCommonImports } from 'src/app/shared/ui/conviva-common';
import { libsCommonImports } from 'src/app/shared/ui/libs-common';

@Component({
  selector: 'app-plano-melhoria-correcao-vinculo',
  templateUrl: './plano-melhoria-correcao-vinculo.component.html',
  styleUrl: './plano-melhoria-correcao-vinculo.component.scss',
  standalone: true,
  imports: [angularCommonImports, convivaCommonImports, libsCommonImports]
})
export class PlanoMelhoriaCorrecaoVinculoComponent implements OnInit {
  private spinnerService = inject(NgxSpinnerService);
  private ConvivaErrors = inject(ConvivaErrorHandlingService);
  private toastr = inject(ToastrService);
  private planoMelhoriaService = inject(PlanoMelhoriaService);
  private authService = inject(AuthService);

  public userLogado: UserLoginSed | null = null;
  public divergencias: PlanoMelhoriaDivergencia[] = [];
  public showModalAjustar = false;
  public divergenciaSelecionada: PlanoMelhoriaDivergencia | null = null;

  public formAjuste = new FormGroup({
    codigoEscolaDestino: new FormControl<number | null>(null, [Validators.required, Validators.min(1)])
  });

  public colunasGrid = [
    {
      title: 'ID do Plano',
      field: 'codigoPlanoMelhoria',
      width: 110,
      hozAlign: 'center',
      headerHozAlign: 'center',
      headerFilter: 'input'
    },
    {
      title: 'Escola de Origem (Backup / Correto)',
      field: 'nomeEscolaOrigem',
      minWidth: 260,
      hozAlign: 'left',
      headerHozAlign: 'center',
      headerFilter: 'input',
      formatter: (cell: any) => {
        const data = cell.getRow().getData();
        return this.formatarEscola(data.codigoEscolaOrigem, data.nomeEscolaOrigem);
      }
    },
    {
      title: 'Escola Atual (Nova Extração / Incorreto)',
      field: 'nomeEscolaAtual',
      minWidth: 260,
      hozAlign: 'left',
      headerHozAlign: 'center',
      headerFilter: 'input',
      formatter: (cell: any) => {
        const data = cell.getRow().getData();
        return this.formatarEscola(data.codigoEscolaAtual, data.nomeEscolaAtual);
      }
    },
    {
      title: 'Ações',
      field: 'acoes',
      cssClass: 'col-acoes',
      hozAlign: 'center',
      headerHozAlign: 'center',
      width: 120,
      headerSort: false,
      formatter: () => `<button type="button" class="btn_primary btn-ajustar-grid">Ajustar</button>`,
      cellClick: (e: any, cell: any) => {
        const target = e.target as HTMLElement;
        if (!target.classList.contains('btn-ajustar-grid')) return;
        this.abrirModalAjustar(cell.getRow().getData());
      }
    }
  ];

  public formatarEscola(codigo?: number, nome?: string): string {
    if (!codigo) return nome?.trim() ?? '';
    const nomeEscola = this.extrairNomeEscolaSemCie(nome);
    return nomeEscola ? `${codigo} - ${nomeEscola}` : `${codigo}`;
  }

  private extrairNomeEscolaSemCie(nome?: string): string {
    if (!nome?.trim()) return '';
    const nomeTrim = nome.trim();
    const match = nomeTrim.match(/^\d+\s*-\s*(.+)$/);
    return match ? match[1].trim() : nomeTrim;
  }

  ngOnInit(): void {
    this.userLogado = this.authService.getUser();
    this.carregarDivergencias();
  }

  public async carregarDivergencias(): Promise<void> {
    this.spinnerService.show();
    const response = await lastValueFrom(this.planoMelhoriaService.getPlanoMelhoriaDivergencias(true))
      .catch((err) => this.ConvivaErrors.handleResultError(err));

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao carregar divergências.');
      this.spinnerService.hide();
      return;
    }

    this.divergencias = response.data ?? [];
    this.spinnerService.hide();
  }

  public abrirModalAjustar(divergencia: PlanoMelhoriaDivergencia): void {
    this.divergenciaSelecionada = divergencia;
    this.formAjuste.patchValue({
      codigoEscolaDestino: divergencia.codigoEscolaOrigem ?? null
    });
    this.showModalAjustar = true;
  }

  public fecharModalAjustar(): void {
    this.showModalAjustar = false;
    this.divergenciaSelecionada = null;
    this.formAjuste.reset();
  }

  public async confirmarAjuste(): Promise<void> {
    if (!this.divergenciaSelecionada || this.formAjuste.invalid || !this.userLogado) return;

    this.spinnerService.show();
    const payload = {
      codigoDivergencia: this.divergenciaSelecionada.codigoDivergencia!,
      codigoEscolaDestino: this.formAjuste.value.codigoEscolaDestino!,
      codigoUsuario: this.userLogado.codigoUsuario,
      nomeUsuario: this.userLogado.nome,
      codigoPerfil: this.userLogado.codigoPerfil,
      nomePerfil: this.userLogado.nomePerfil
    };

    const response = await lastValueFrom(this.planoMelhoriaService.setCorrigirVinculoPlanoMelhoria(payload))
      .catch((err) => this.ConvivaErrors.handleResultError(err));

    if (!response?.isSucess) {
      this.toastr.error(response?.message ?? 'Erro ao ajustar vínculo.');
      this.spinnerService.hide();
      return;
    }

    this.toastr.success(response.message ?? 'Vínculo ajustado com sucesso.');
    this.fecharModalAjustar();
    await this.carregarDivergencias();
    this.spinnerService.hide();
  }
}
